import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  HostListener,
  ViewChild,
} from "@angular/core";
import {
  FormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormControl,
} from "@angular/forms";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { validateNonRequiredPhoneNumber } from "src/app/shared/validators/phone-number.validator";
import {
  GetEmployeeAction,
  UpdateEmployeeAction,
  UPDATE_EMPLOYEE_FAILURE,
  UPDATE_EMPLOYEE_SUCCESS,
} from "../employees.actions";
import { getEmployeeSelector } from "../employees.reducer";
import { Employee, EmployeeVcard } from "../employees.interface";
import { getEmployeeSettingsSelector } from "../../settings/settings.reducer";
import { EmployeeSetting } from "../../settings/settings.interface";
import { GetEmployeeSettingsAction } from "../../settings/settings.actions";
import { StartCheckingForInstallationInProgressAction } from "../../signature-installation-state/signature-installation-state.actions";
import { parseFooterTemplate } from "../../shared/utils/footers.util";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../../shared/toastr.service";
import { saveAs } from "file-saver";
import { AdvancedSettings } from "../../settings/settings.interface";
import { getAdvancedSettingsSelector } from "../../settings/settings.reducer";
import { GetAdvancedSettingsAction } from "../../settings/settings.actions";
import { BADGES } from "../../shared/badges/badges.data";
import { Badge } from "../../shared/badges/badges.interface";
import { combineLatest, Subject } from "rxjs";
import { take, takeUntil, filter } from "rxjs/operators";
import { Actions, ofType } from "@ngrx/effects";
import { EmployeesService } from "../employees.service";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import { AuthService } from "../../auth/auth.service";
import { EmployeeVcardComponent } from "../../shared/employee-vcard/employee-vcard.component";

@Component({
  selector: "app-employee-edit-dialog",
  templateUrl: "./employee-edit-dialog.component.html",
  styleUrls: ["./employee-edit-dialog.component.scss"],
  standalone: false,
})
export class EmployeeEditDialogComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  employee: Employee;
  employeeSettings: EmployeeSetting[];
  dynamicEmployeeSettings: EmployeeSetting[] = [];
  advancedSettings: AdvancedSettings;
  parsedSignature: string;
  previewType: "external" | "internal" = "external";
  isLoading = true;
  saving = false;
  availableBadges: Badge[] = [];
  selectedBadges = [];
  private destroy$ = new Subject<void>();
  employeeId: string;
  isSyncing = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  branchId: string | null = null;
  branchVariables: Record<string, unknown> = {};
  vcardEntries: EmployeeVcard[] = [];
  vcardLoadVersion = 0;
  genderOptions: Array<{ value: "male" | "female"; labelKey: string }> = [
    { value: "male", labelKey: "employee.genderMale" },
    { value: "female", labelKey: "employee.genderFemale" },
  ];
  @ViewChild(EmployeeVcardComponent) employeeVcard?: EmployeeVcardComponent;
  constructor(
    public dialogRef: MatDialogRef<EmployeeEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { employeeId: string },
    private translate: TranslateService,
    private toastr: ToastrService,
    private store: Store,
    private actions$: Actions,
    private employeesService: EmployeesService,
    private router: Router,
    public auth: AuthService,
  ) {
    this.employeeId = data.employeeId;
    this.dialogRef.updateSize("90vw", "90vh");
  }

  get namePronunciationEnabled(): boolean {
    return !!this.auth.appUser?.optionalInterfaces?.namePronunciation;
  }

  get showNameProfile(): boolean {
    return this.namePronunciationEnabled || this.auth.isSuperAdmin;
  }

  get nameProfileInactive(): boolean {
    return this.auth.isSuperAdmin && !this.namePronunciationEnabled;
  }

  ngOnInit(): void {
    this.initializeForm();

    this.store.dispatch(new GetEmployeeAction(this.employeeId));
    this.store.dispatch(new GetEmployeeSettingsAction());
    this.store.dispatch(new GetAdvancedSettingsAction());

    combineLatest([
      this.store
        .select(getEmployeeSelector)
        .pipe(
          filter(
            (emp): emp is Employee =>
              !!emp && emp.employeeId === this.employeeId,
          ),
        ),
      this.store.select(getEmployeeSettingsSelector),
      this.store
        .select(getAdvancedSettingsSelector)
        .pipe(filter((settings) => settings !== null)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([employee, settings, advancedSettings]) => {
        this.applyEmployeeState(employee, settings, advancedSettings);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyEmployeeState(
    employee: Employee,
    settings: EmployeeSetting[],
    advancedSettings: AdvancedSettings,
  ): void {
    this.employee = employee;
    this.employeeSettings = settings ?? [];
    this.dynamicEmployeeSettings = this.employeeSettings.filter(
      (setting) => !setting.entityBound,
    );
    this.advancedSettings = advancedSettings;
    this.pendingRemoveAvatar = false;
    this.initializeDynamicFields();
    this.configureProfileFields();
    this.fillForm(employee);
    this.initializeBadges();
    this.updateParsedSignature();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.isLoading = false;
  }

  private initializeForm(): void {
    this.form = new UntypedFormGroup({
      excludedFromSignatures: new UntypedFormControl("", (control) =>
        Validators.required(control),
      ),
      email: new UntypedFormControl({ value: "", disabled: true }),
      givenName: new UntypedFormControl(""),
      familyName: new UntypedFormControl(""),
      userAbout: new UntypedFormControl(""),
      gender: new UntypedFormControl(null),
      countryOfOrigin: new UntypedFormControl(""),
      dynamicVariables: new UntypedFormGroup({}),
      vcard: new FormArray([]),
    });
  }

  private initializeDynamicFields(): void {
    const dynamicGroup = this.dynamicVariables;
    Object.keys(dynamicGroup.controls).forEach((key) => {
      dynamicGroup.removeControl(key);
    });

    this.dynamicEmployeeSettings.forEach((setting) => {
      const validators = [];
      if (setting.mandatory) {
        validators.push((control: FormControl) => Validators.required(control));
      }
      if (["phoneNumber", "mobileNumber"].includes(setting.name)) {
        validators.push((control: FormControl) =>
          validateNonRequiredPhoneNumber(control),
        );
      }

      dynamicGroup.addControl(
        setting.name,
        new UntypedFormControl(
          { value: "", disabled: !setting.overridable },
          validators,
        ),
      );
    });
  }

  private configureProfileFields(): void {
    this.employeeSettings
      .filter((setting) => setting.entityBound && setting.name !== "email")
      .forEach((setting) => {
        const control = this.form.get(setting.name);
        if (!control) {
          return;
        }
        control.setValidators(
          setting.mandatory ? [(c: FormControl) => Validators.required(c)] : [],
        );
        const syncedNameLock =
          (setting.name === "givenName" || setting.name === "familyName") &&
          this.employee?.dataSource !== "LOCAL";
        if ((setting.overridable ?? true) && !syncedNameLock) {
          control.enable({ emitEvent: false });
        } else {
          control.disable({ emitEvent: false });
        }
        control.updateValueAndValidity({ emitEvent: false });
      });
  }

  profileFieldLabel(name: string): string {
    const label = this.employeeSettings
      ?.find((row) => row.name === name)
      ?.label?.trim();
    if (label) {
      return label;
    }
    return this.translate.instant(
      name === "email" ? "employee.email" : `employee.${name}`,
    ) as string;
  }

  private fillForm(emp: Employee): void {
    const dynamicValues: Record<string, unknown> = {};
    if (this.dynamicEmployeeSettings?.length) {
      this.dynamicEmployeeSettings.forEach((setting) => {
        dynamicValues[setting.name] =
          emp.dynamicVariables?.[setting.name] || "";
      });
    } else {
      Object.assign(
        dynamicValues,
        Object.fromEntries(
          Object.entries(emp.dynamicVariables || {}).filter(
            ([key]) =>
              !this.employeeSettings.some(
                (setting) => setting.name === key && setting.entityBound,
              ),
          ),
        ),
      );
    }

    const country = String(emp.countryOfOrigin ?? "")
      .trim()
      .toUpperCase();

    this.form.patchValue(
      {
        excludedFromSignatures: emp.excludedFromSignatures,
        email: emp.email || "",
        givenName: emp.givenName || "",
        familyName: emp.familyName || "",
        userAbout: emp.userAbout || "",
        gender:
          emp.gender === "male" || emp.gender === "female" ? emp.gender : null,
        countryOfOrigin: /^[A-Z]{2}$/.test(country) ? country : "",
        dynamicVariables: dynamicValues,
      },
      { emitEvent: false },
    );

    this.branchId = emp.branchId || null;
    this.branchVariables = { ...(emp.branchVariables || {}) };

    if (emp.signatureInternal && emp.signature) {
      this.previewType = "external";
    } else if (emp.signatureInternal) {
      this.previewType = "internal";
    } else {
      this.previewType = "external";
    }

    this.selectedBadges = emp.badges || [];
    this.vcardEntries = [...(emp.vcard || [])];
    this.vcardLoadVersion++;
  }

  private buildPayload(installSignature: boolean): Employee {
    const formValue = this.form.getRawValue() as Partial<Employee>;
    const country = String(formValue.countryOfOrigin ?? "")
      .trim()
      .toUpperCase();
    const filteredDynamic = Object.fromEntries(
      Object.entries(formValue.dynamicVariables || {}).filter(
        ([key]) =>
          !this.employeeSettings.some(
            (setting) => setting.name === key && setting.entityBound,
          ),
      ),
    );
    const dynamicVariables = Object.fromEntries(
      Object.entries(filteredDynamic).map(([k, v]) => [k, v || null]),
    );
    const payload: Employee = {
      ...this.employee,
      ...formValue,
      employeeId: this.employeeId,
      email: this.employee.email,
      givenName: formValue.givenName || null,
      familyName: formValue.familyName || null,
      userAbout: formValue.userAbout || null,
      gender:
        formValue.gender === "male" || formValue.gender === "female"
          ? formValue.gender
          : null,
      countryOfOrigin: /^[A-Z]{2}$/.test(country) ? country : null,
      vcard: this.employeeVcard?.getPayload() ?? [],
      badges: this.selectedBadges as Badge[],
      installSignature,
      branchId: this.branchId,
      branchVariables:
        Object.keys(this.branchVariables).length > 0
          ? this.branchVariables
          : undefined,
      dynamicVariables,
    } as unknown as Employee;
    if (this.pendingRemoveAvatar) {
      payload.thumbnailPhotoUrl = null;
      payload.localAvatar = false;
    }
    return payload;
  }

  private initializeBadges(): void {
    this.availableBadges = [...BADGES];

    (this.employee.badges || []).forEach((badge) => {
      if (!this.availableBadges.find((x) => x.label === badge.label)) {
        this.availableBadges.push(badge);
      }
    });

    this.selectedBadges = this.employee.badges || [];
  }

  getCurrentSignature(): string {
    if (this.employee.signature && this.employee.signatureInternal) {
      return this.previewType === "internal"
        ? this.employee.signatureInternal
        : this.employee.signature;
    }
    return this.employee.signatureInternal || this.employee.signature;
  }

  updateParsedSignature(): void {
    if (this.employee) {
      this.parsedSignature = parseFooterTemplate(this.getCurrentSignature(), {
        ...this.employee,
        ...this.form.getRawValue(),
      } as Record<string, string>);
    }
  }

  onBranchChange(event: {
    branchId: string | null;
    branchVariables: Record<string, unknown>;
  }): void {
    const nextBranchId = event.branchId;
    const nextVariables = event.branchVariables || {};
    const sameBranch = this.branchId === nextBranchId;
    const sameVariables =
      JSON.stringify(this.branchVariables || {}) ===
      JSON.stringify(nextVariables);

    if (sameBranch && sameVariables) {
      return;
    }

    this.branchId = nextBranchId;
    this.branchVariables = nextVariables;
    this.form.markAsDirty();
  }

  save(): void {
    if (this.saving) {
      return;
    }
    this.errorStateMatcher.submitAttempted = true;
    if (!this.form.valid || !(this.employeeVcard?.isValid() ?? true)) {
      return;
    }

    this.saving = true;
    const payload = this.buildPayload(false);
    this.store.dispatch(new UpdateEmployeeAction(payload));
    this.actions$
      .pipe(
        ofType(UPDATE_EMPLOYEE_SUCCESS, UPDATE_EMPLOYEE_FAILURE),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((action: { type: string; payload?: unknown }) => {
        this.saving = false;
        if (action.type === UPDATE_EMPLOYEE_FAILURE) {
          return;
        }
        this.dialogRef.close({
          success: true,
          employee: action.payload ?? payload,
        });
      });
  }

  saveAndReinstall(): void {
    if (this.saving) {
      return;
    }
    this.errorStateMatcher.submitAttempted = true;
    if (!this.form.valid || !(this.employeeVcard?.isValid() ?? true)) {
      return;
    }

    this.saving = true;
    const payload = this.buildPayload(true);
    this.store.dispatch(new UpdateEmployeeAction(payload));
    this.store.dispatch(new StartCheckingForInstallationInProgressAction());
    this.actions$
      .pipe(
        ofType(UPDATE_EMPLOYEE_SUCCESS, UPDATE_EMPLOYEE_FAILURE),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((action: { type: string; payload?: unknown }) => {
        this.saving = false;
        if (action.type === UPDATE_EMPLOYEE_FAILURE) {
          return;
        }
        this.dialogRef.close({
          success: true,
          employee: action.payload ?? payload,
          reinstall: true,
        });
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  @HostListener("document:keydown.escape", ["$event"])
  onEscapeKey(event: KeyboardEvent): void {
    event.preventDefault();
    this.cancel();
  }

  copySignatureToClipboard(): void {
    void navigator.clipboard.writeText(this.parsedSignature);
    this.toastr.success(
      this.translate.instant("employee.copySignatureSuccess") as string,
    );
  }

  copySignaturePlainToClipboard(): void {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = this.parsedSignature;

    const htmlContent = tempDiv.innerHTML;

    void navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([htmlContent], { type: "text/html" }),
        "text/plain": new Blob([tempDiv.textContent || ""], {
          type: "text/plain",
        }),
      }),
    ]);
    this.toastr.success(
      this.translate.instant("employee.copySignatureSuccess") as string,
    );
  }

  downloadSignatureOutlook(): void {
    const html = this.parsedSignature;
    const [local, domain] = this.employee.email.split("@");
    const fn = `${local.replace(/\./g, "_")}_${domain.replace(/\./g, "_")}.htm`;
    const blob = new Blob([html], { type: "text/html" });
    saveAs(blob, fn);
  }

  get dynamicVariables(): UntypedFormGroup {
    return this.form.get("dynamicVariables") as UntypedFormGroup;
  }

  onAvatarChanged(): void {
    this.store.dispatch(new GetEmployeeAction(this.employeeId));
  }

  pendingRemoveAvatar = false;

  removeAvatar(): void {
    if (!this.employee?.picture && !this.pendingRemoveAvatar) {
      return;
    }
    this.pendingRemoveAvatar = true;
    this.form.markAsDirty();
  }

  get displayAvatarUrl(): string | null {
    if (this.pendingRemoveAvatar) {
      return null;
    }
    return this.employee?.picture ?? null;
  }

  forceSyncEmployee(): void {
    if (
      !this.employee ||
      this.isSyncing ||
      !this.canForceSyncEmployee(this.employee.dataSource)
    ) {
      return;
    }

    this.isSyncing = true;

    this.employeesService.syncEmployee(this.employeeId).subscribe({
      next: (result) => {
        this.isSyncing = false;
        if (!result.success) {
          this.toastr.error(
            this.translate.instant("employee.syncFailed") as string,
          );
          return;
        }

        this.toastr.success(
          this.translate.instant("employee.syncCompleted") as string,
        );
        this.employeesService.getEmployee(this.employeeId).subscribe({
          next: (response) => {
            this.applySyncedEmployee(response);
          },
          error: () => {
            this.store.dispatch(new GetEmployeeAction(this.employeeId));
          },
        });
      },
      error: () => {
        this.isSyncing = false;
        this.toastr.error(
          this.translate.instant("employee.syncError") as string,
        );
      },
    });
  }

  private applySyncedEmployee(synced: Partial<Employee>): void {
    this.employee = { ...this.employee, ...synced };

    const dynamicValues: Record<string, unknown> = {};
    if (this.dynamicEmployeeSettings?.length) {
      this.dynamicEmployeeSettings.forEach((setting) => {
        dynamicValues[setting.name] =
          this.employee.dynamicVariables?.[setting.name] || "";
      });
    } else {
      Object.assign(
        dynamicValues,
        Object.fromEntries(
          Object.entries(this.employee.dynamicVariables || {}).filter(
            ([key]) =>
              !this.employeeSettings.some(
                (setting) => setting.name === key && setting.entityBound,
              ),
          ),
        ),
      );
    }

    const country = String(this.employee.countryOfOrigin ?? "")
      .trim()
      .toUpperCase();

    this.form.patchValue(
      {
        email: this.employee.email || "",
        givenName: this.employee.givenName || "",
        familyName: this.employee.familyName || "",
        userAbout: this.employee.userAbout || "",
        gender:
          this.employee.gender === "male" || this.employee.gender === "female"
            ? this.employee.gender
            : null,
        countryOfOrigin: /^[A-Z]{2}$/.test(country) ? country : "",
        dynamicVariables: dynamicValues,
      },
      { emitEvent: false },
    );
    this.configureProfileFields();
  }

  canForceSyncEmployee(dataSource?: Employee["dataSource"]): boolean {
    return !!dataSource && dataSource !== "LOCAL" && dataSource !== "SAMPLE";
  }

  getEmployeeDataSourceLabel(dataSource?: Employee["dataSource"]): string {
    switch (dataSource) {
      case "GOOGLE":
        return "employee.google";
      case "MICROSOFT":
        return "employee.microsoft";
      case "CALAMARI":
        return "employee.calamari";
      default:
        return "employee.local";
    }
  }

  openDigitalCard(): void {
    const userId = this.employee?.employeeId;
    if (!userId) return;
    this.dialogRef.close();
    void this.router.navigate(["/digital-cards"], {
      queryParams: { user: userId },
    });
  }
}
